import React, { useEffect, useMemo, useState } from 'react'
import { Button, Select, notification } from 'antd'
import { LinkOutlined } from '@ant-design/icons'
import { isNil } from 'lodash'
import { useSelector } from 'react-redux'
import { selectors as companySelectors } from 'store/modules/companies'
import { getMyClients } from 'enrollment-platform/api/persons'
import { SimpleCustomerDto } from 'enrollment-platform/api/persons/dto/simple-customer.dto'
import { getAdminSettings } from 'enrollment-platform/utils/adminSettings'
import { useMatchDeliveryMutation } from '../../api/invoice-delivery-api-client'
import { InvoiceDeliveryItemDto } from '../../api/dto/invoice-delivery.dto'
import { DrawerNoEmailWarning } from './InvoiceDeliveryDrawer.styles'
import styles from './MatchCompanySection.module.scss'

interface CompanyStoreItem {
  id: string
  isTestClient?: boolean
}

interface MatchCompanySectionProps {
  deliveryId: string
  open: boolean
  onMatched: (updated: InvoiceDeliveryItemDto) => void
}

const MatchCompanySection: React.FC<MatchCompanySectionProps> = ({
  deliveryId,
  open,
  onMatched,
}) => {
  const [clients, setClients] = useState<SimpleCustomerDto[]>([])
  const [clientsLoading, setClientsLoading] = useState(false)
  const [selectedClientId, setSelectedClientId] = useState<string | undefined>(
    undefined
  )

  const companiesFromStore = useSelector(
    companySelectors.companyCollectionSelector
  )

  useEffect(() => {
    if (!open) return
    let cancelled = false
    setClientsLoading(true)
    getMyClients()
      .then((data) => {
        if (!cancelled) setClients(data)
      })
      .catch(() => {
        if (!cancelled) setClients([])
      })
      .finally(() => {
        if (!cancelled) setClientsLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [open])

  useEffect(() => {
    setSelectedClientId(undefined)
  }, [deliveryId])

  const { showTestData } = getAdminSettings().finance
  const testClientIds = useMemo(
    () =>
      new Set(
        (companiesFromStore as CompanyStoreItem[])
          .filter((c) => c.isTestClient)
          .map((c) => c.id)
      ),
    [companiesFromStore]
  )
  const visibleClients = useMemo(
    () =>
      showTestData ? clients : clients.filter((c) => !testClientIds.has(c.id)),
    [clients, showTestData, testClientIds]
  )

  const matchMutation = useMatchDeliveryMutation({
    onSuccess: (updated) => {
      setSelectedClientId(undefined)
      notification.success({ message: 'Company matched successfully' })
      onMatched(updated)
    },
    onError: (error: unknown) => {
      const data = (
        error as { response?: { data?: { message?: string; error?: string } } }
      )?.response?.data
      const description =
        typeof data?.message === 'string'
          ? data.message
          : (data?.error ?? 'An unexpected error occurred')
      notification.error({ message: 'Failed to match company', description })
    },
  })

  const handleMatch = () => {
    if (!selectedClientId) return
    const client = visibleClients.find((c) => c.id === selectedClientId)
    if (!client) return
    matchMutation.mutate({
      id: deliveryId,
      body: { clientId: client.id, clientName: client.name },
    })
  }

  return (
    <>
      <DrawerNoEmailWarning>
        This file could not be automatically matched to a known company. Select
        the correct company below.
      </DrawerNoEmailWarning>
      <div className={styles.matchRow}>
        <Select
          showSearch
          placeholder={
            clientsLoading ? 'Loading companies…' : 'Search company…'
          }
          loading={clientsLoading}
          disabled={clientsLoading}
          className={styles.selectFlex}
          value={selectedClientId}
          onChange={setSelectedClientId}
          optionFilterProp="children"
          filterOption={(input, option) =>
            String(option?.children ?? '')
              .toLowerCase()
              .includes(input.toLowerCase())
          }
          size="small"
        >
          {visibleClients.map((c) => (
            <Select.Option key={c.id} value={c.id}>
              {c.name}
            </Select.Option>
          ))}
        </Select>
        <Button
          type="primary"
          size="small"
          htmlType="button"
          disabled={isNil(selectedClientId)}
          loading={matchMutation.isPending}
          onClick={handleMatch}
          icon={<LinkOutlined />}
        >
          Match
        </Button>
      </div>
    </>
  )
}

export { MatchCompanySection }
